import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  type PropsWithChildren,
} from "react";
import {
  createORPCFrontendClient,
  type ORPCClient,
  type ORPCClientOptions,
} from "./orpc-client.ts";

type AccessTokenProvider = ORPCClientOptions["accessToken"];

function createQueryUtils(client: ORPCClient) {
  return createTanstackQueryUtils(client);
}

export type ORPCQueryUtils = ReturnType<typeof createQueryUtils>;

const ORPCClientContext = createContext<ORPCClient | null>(null);
const ORPCQueryUtilsContext = createContext<ORPCQueryUtils | null>(null);

export interface ORPCReactProviderProps extends PropsWithChildren {
  apiURL?: ORPCClientOptions["apiURL"];
  accessToken?: AccessTokenProvider;
}

function apiURLDependencyKey(apiURL: ORPCClientOptions["apiURL"]): string {
  if (!apiURL) return "";
  return apiURL instanceof URL ? apiURL.href : apiURL;
}

export function ORPCReactProvider(props: ORPCReactProviderProps) {
  const { accessToken, apiURL, children } = props;

  const accessTokenRef = useRef<AccessTokenProvider>(accessToken);
  accessTokenRef.current = accessToken;

  const stableAccessTokenProvider = useCallback(() => {
    const currentProvider = accessTokenRef.current;
    const resolved = typeof currentProvider === "function" ? currentProvider() : currentProvider;

    if (!resolved) return undefined;

    const trimmed = resolved.trim();
    if (trimmed.length === 0) return undefined;

    return trimmed;
  }, []);

  const apiURLKey = apiURLDependencyKey(apiURL);

  const client = useMemo(
    () =>
      createORPCFrontendClient({
        accessToken: stableAccessTokenProvider,
        apiURL,
      }),
    [apiURLKey, stableAccessTokenProvider],
  );

  const queryUtils = useMemo(() => createQueryUtils(client), [client]);

  return (
    <ORPCClientContext.Provider value={client}>
      <ORPCQueryUtilsContext.Provider value={queryUtils}>{children}</ORPCQueryUtilsContext.Provider>
    </ORPCClientContext.Provider>
  );
}

export function useORPCClient(): ORPCClient {
  const client = useContext(ORPCClientContext);

  if (!client) {
    throw new Error("useORPCClient must be used within an ORPCReactProvider instance.");
  }

  return client;
}

export function useORPC(): ORPCQueryUtils {
  const queryUtils = useContext(ORPCQueryUtilsContext);

  if (!queryUtils) {
    throw new Error("useORPC must be used within an ORPCReactProvider instance.");
  }

  return queryUtils;
}
